import React, { useEffect, useState } from 'react';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi'; // 1. Import the new hook
import CreateSurveyDialog from '../components/ui/CreateSurveyDialog';
import { toast } from "sonner"
import SurveyList from '../components/ui/SurveyList';
import Spinner from '../components/ui/Spinner';

import API_CONFIG from '../config/api';
const BASE_URL = API_CONFIG.BASE_URL;

function DashboardPage() {
	// 2. Get the special fetch function from our hook
	const { authenticatedFetch } = useAuthenticatedApi();

	// 3. Use more descriptive state for handling the API call
	const [userData, setUserData] = useState(null);
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(true);
	const [refreshKey, setRefreshKey] = useState(0);
	
	const [activeTab, setActiveTab] = useState('active');

	const testHandleSurveySubmit = async (response) => {
		if (response.ok) {
			// Parse the JSON response to get the structured data
			const result = await response.json();
			
			// Format AI generation status
			const getAIStatus = (status) => {
				switch(status) {
					case 'completed': return 'Complete';
					case 'failed': return 'Failed';
					case 'not': return 'Not activated';
					default: return 'Unknown';
				}
			};
			
			// Create detailed success message
			const detailedMessage = `
								Survey Created: ✅ True
								AI Generation: ${getAIStatus(result.ai_generation_status)}
								${result.title ? `Title: "${result.title}"` : ''}
								Message: ${result.message || 'Survey created successfully'}
										`.trim();
			toast(detailedMessage, {
				duration: 6000, // Longer duration for more content
				style: {
					background: '#f0fdf4',
					border: '1px solid #22c55e',
					borderRadius: '8px',
					fontSize: '16px',
					fontFamily: 'monospace',
					padding: '16px',
					minWidth: '400px',
					whiteSpace: 'pre-line'
				}
			});
			
			setRefreshKey(prevKey => prevKey + 1);
		} else {
			// Handle error cases
			try {
				const errorResult = await response.json();
				
				const errorMessage = `
									Survey Created: ❌ False
									AI Generation: ${getAIStatus(errorResult.ai_generation_status || 'not')}
									Message: ${errorResult.message || 'Unknown error occurred'}
												`.trim();
				
				toast(errorMessage, {
					duration: 8000,
					style: {
						background: '#fef2f2',
						border: '1px solid #ef4444',
						borderRadius: '8px',
						fontSize: '16px',
						fontFamily: 'monospace',
						padding: '16px',
						minWidth: '400px',
						whiteSpace: 'pre-line'
					}
				});
			} catch {
				// Fallback if response doesn't have JSON
				const fallbackMessage = `
									Survey Created: ❌ False
									AI Generation: Unknown
									Message: Error ${response.status}: ${response.statusText}
												`.trim();
				
				toast(fallbackMessage, {
					duration: 8000,
					style: {
						background: '#fef2f2',
						border: '1px solid #ef4444',
						borderRadius: '8px',
						fontSize: '16px',
						fontFamily: 'monospace',
						padding: '16px',
						minWidth: '400px',
						whiteSpace: 'pre-line'
					}
				});
			}
		}
	};
	
	// Helper function extracted outside to avoid duplication
	const getAIStatus = (status) => {
		switch(status) {
			case 'completed': return '✅ Complete';
			case 'failed': return '❌ Failed';
			case 'not': return '⚪ Not activated';
			default: return '❓ Unknown';
		}
	};

	useEffect(() => {
		const fetchUserData = async () => {
			try {
				setLoading(true);
				setError('');

				// 4. Use authenticatedFetch to call your root endpoint
				// It automatically handles the token and refreshing.
				const response = await authenticatedFetch(`${BASE_URL}/`, {}, 'GET');

				if (!response.ok) {
					// Handle non-401 errors that the hook doesn't handle, like 404 or 500
					const errorData = await response.json().catch(() => ({ detail: 'An unknown error occurred.' }));
					throw new Error(errorData.detail || `Server error: ${response.status}`);
				}

				const result = await response.json();
				// Your API returns { "user": ... }, so we'll store the user object
				setUserData(result.user);

			} catch (err) {
				// This single catch block now handles everything:
				// - Initial fetch errors
				// - "Session expired. Please log in again." error from the hook if refresh fails
				console.error("Failed to fetch protected data:", err.message);
				setError(err.message);
				setUserData(null); // Clear any potentially stale data
				// NOTE: The hook's refresh logic already calls logout() on failure,
				// so you don't need to call it again here. The ProtectedRoute will handle the redirect.

			} finally {
				setLoading(false);
			}
		};

		fetchUserData();
		
	}, []);
	// 5. Render UI based on the loading, error, and data states
	if (loading) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen gap-2">
				<Spinner text="Loading content..." size="md" className="mb-4" />
			</div>
		);
	}

	return (
		<div className="p-6">
		  {error && (
			<div role="alert" className="alert alert-error mb-4">
			  <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
			  </svg>
			  <span>Error: {error}</span>
			</div>
		  )}
	  
		  {userData && (
			<>
			  <h2 className="text-2xl font-bold flex items-center justify-between">
				Welcome to your dashboard {userData.username}!
				<CreateSurveyDialog onSurveyCreated={testHandleSurveySubmit} />
			  </h2>
			  <p className="mb-4">From here you can monitor your deployed and editable survey, find your ordered dataset and much more ...</p>
			  
			  {/* Main Content Area - Tabbed Interface */}
			  <div className="mt-8 bg-white rounded-lg shadow-sm border">
				{/* REMOVE overflow-hidden from here ☝️ */}
				
				{/* Tab Navigation */}
				<div className="border-b border-gray-200">
				  <nav className="flex space-x-8" aria-label="Tabs">
					<button
					  onClick={() => setActiveTab('active')}
					  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
						activeTab === 'active'
						  ? 'text-gray-800 text-blue-600'
						  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
					  }`}
					>
					  Active Surveys
					</button>
					<button
					  onClick={() => setActiveTab('draft')}
					  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
						activeTab === 'draft'
						  ? 'text-gray-800 text-blue-600'
						  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
					  }`}
					>
					  Draft Surveys
					</button>
					<button
					  onClick={() => setActiveTab('archived')}
					  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
						activeTab === 'archived'
						  ? 'text-gray-800 text-blue-600'
						  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
					  }`}
					>
					  Archived Surveys
					</button>
				  </nav>
				</div>
	  
				{/* Tab Content - THIS is where you control overflow */}
				<div className="h-150 overflow-y-auto overflow-x-hidden pb-20">
				  {/* ☝️ Set a fixed height and enable vertical scrolling */}
				  
				  <div className="p-6">
					{activeTab === 'active' && (
					  <div>
						<h3 className="text-2xl font-semibold mb-4 text-gray-800">Active surveys</h3>
						<SurveyList refreshTrigger={refreshKey} surveyStatus={"active"} />
					  </div>
					)}
					{activeTab === 'draft' && (
					  <div>
						<h3 className="text-2xl font-semibold mb-4 text-gray-800">Draft surveys</h3>
						<SurveyList refreshTrigger={refreshKey} surveyStatus={"draft"} />
					  </div>
					)}
					{activeTab === 'archived' && (
					  <div>
						<h3 className="text-2xl font-semibold mb-4 text-gray-800">Archived surveys</h3>
						<SurveyList refreshTrigger={refreshKey} surveyStatus={"archived"} />
					  </div>
					)}
				  </div>
				</div>
			  </div>
			</>
		  )}
		</div>
	  );
}

export default DashboardPage;